import { notification } from 'antd'
import { AreaContext } from 'pages/ProjectsPage/WorkspacePage/components/Common/AreaContext'
import { useCallback, useContext } from 'react'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { useHoveredActionMutationService } from '../../../services'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import { getAllChildrenFromHoveredNode } from '../../../utils/hoveredActionUtils'

/**
 * Internal hook: Handles single content deactivation with optimistic update
 */
export const usePTContentDeactivation = () => {
    // Use Workspace2Store for projectId (performance optimized), AreaContext for nugget
    const projectId = useWorkspace2Store((state) => state.projectId)
    const { nugget } = useContext(AreaContext)
    const {
        mutateDeactivationWithApolloClient,
    } = useHoveredActionMutationService()

    const deactivateSingleContent = useCallback(
        async (
            contentItem: Partial<ContentItemViewNodesType>,
            selectedProjectElementNodeId?: string
        ) => {
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes
            const setContentItemViewNodes =
                storeState.actions.setContentItemViewNodes

            // Use getAllChildrenFromHoveredNode to deactivate parent AND all children
            // This ensures the optimistic UI update matches the backend behavior
            const updatedNodes = getAllChildrenFromHoveredNode(
                contentItem,
                latestNodes,
                true // isHidden = true (deactivating)
            )

            if (updatedNodes) {
                setContentItemViewNodes(updatedNodes)
            }

            const input = {
                documentTemplateViewId: contentItem.documentTemplateViewId,
                isHidden: true,
                contentItemOwnerId: contentItem.dtvItemId,
                projectId,
                nodeId: selectedProjectElementNodeId,
                contentItemId: contentItem.id,
                projectNuggetId: nugget?.id,
                isTrackChangeToggleOn: true,
            }

            mutateDeactivationWithApolloClient(input).catch(() => {
                // Rollback optimistic update
                setContentItemViewNodes(latestNodes)

                notification.error({
                    message: 'Failed to Deactivate Content',
                    description:
                        'Could not deactivate the content item. Please try again.',
                    duration: 5,
                    placement: 'topRight',
                })
            })
        },
        [projectId, nugget?.id, mutateDeactivationWithApolloClient]
    )

    return { deactivateSingleContent }
}
